import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Button, IconButton, Paper, Typography } from '@mui/material'
import { CellEditingStoppedEvent, ICellRendererParams } from 'ag-grid-community'
import { ComponentProps } from 'react'
import { useMutation, useQuery } from 'urql'
import useEventStream from '../contexts/EventStream/useEventStream'
import { graphql } from '../gql'
import { serializeDates } from '../utils/serialize-dates'
import { ThemedAgGrid } from './ThemedAgGrid'
import { ClientIcon } from './icon-catalog'

const listQuery = graphql(`
  query ClientsList {
    Clients {
      id
      name
      website
      dateCreated
    }
  }
`)

export default function ClientsList(props: {
  onAddClick: () => void
  boxProps?: ComponentProps<typeof Box>
}) {
  const [result, refetch] = useQuery({ query: listQuery, requestPolicy: 'network-only' })
  const { data, fetching, error } = result

  const [deleteResult, deleteClient] = useMutation(
    graphql(`
      mutation ClientsListDeleteClient($id: Int!) {
        DeleteClient(id: $id) {
          id
        }
      }
  `),
  )

  const [updateResult, updateClient] = useMutation(
    graphql(`
      mutation ClientsListUpdateClient($id: Int!, $input: UpdateClientInput!) {
        UpdateClient(id:$id, input: $input) {
          name
          website
          id
        }
      }
  `),
  )

  const handleCellEditingStopped = async (e: CellEditingStoppedEvent) => {
    const { data, colDef } = e
    if (!data) return
    const id = data.id
    const field = colDef.field
    if (!field) return
    const input = { [field]: e.newValue }

    const resp = await updateClient({ id, input: input as any })
    if (resp.error) {
      alert(`Error updating client: ${resp.error}`)
    }
  }

  const handleDeleteClick = async (id: number) => {
    await deleteClient({ id })
  }

  useEventStream({
    onMessage: (message) => {
      if (message?.event?.startsWith('client.')) {
        setTimeout(() => refetch({ requestPolicy: 'network-only' }), 100)
      }
    },
  })

  if (fetching) return <p>Loading...</p>
  if (error) return <p>Oh no... {error.message}</p>
  if (!data || !data.Clients || !data.Clients.length) {
    return <EmptyList onAddClick={props.onAddClick} />
  }

  const rowData = data.Clients.map(serializeDates)

  type TData = (typeof rowData)[0]

  return (
    <Box flex="1" {...(props.boxProps || {})}>
      <ThemedAgGrid
        rowData={rowData}
        // groupHeaderHeight={20}
        gridOptions={{
          autoSizeStrategy: {
            type: 'fitGridWidth',
            columnLimits: [
              {
                colId: 'id',
                minWidth: 50,
                maxWidth: 60,
              },
              {
                colId: 'actions',
                minWidth: 90,
                maxWidth: 90,
              },
            ],
          },
        }}
        onCellEditingStopped={handleCellEditingStopped}
        columnDefs={[
          { field: 'id' },
          { field: 'name', filter: true, editable: true },
          { field: 'website', filter: true, editable: true },
          { field: 'dateCreated', filter: true },
          {
            colId: 'actions',
            headerName: 'Actions',
            resizable: false,
            sortable: false,
            cellRenderer: (d: ICellRendererParams<TData>) => {
              return (
                <IconButton type="button" onClick={() => handleDeleteClick(d.data!.id)}>
                  <DeleteIcon />
                </IconButton>
              )
            },
          },
        ]}
      />
    </Box>
  )
}

const EmptyList = (props: { onAddClick: () => void }) => {
  return (
    <Paper sx={{ minHeight: 200, height: '100%', p: 2 }}>
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
        <ClientIcon sx={{ fontSize: 60, color: (theme) => theme.palette.grey[300] }} />
        <Typography variant="caption" gutterBottom>
          You don't have any clients yet.
        </Typography>
        <Button variant="contained" color="primary" sx={{ mt: 2 }} startIcon={<AddIcon />} onClick={props.onAddClick}>
          Add a Client
        </Button>
      </Box>
    </Paper>
  )
}
